import {
    configureCache,
    GrowthBook,
    type Experiment,
    type FeatureDefinition,
    type Result,
} from "@growthbook/growthbook-react";
import stringHash from "string-hash";

import {isNonNullable} from "@khanacademy/wonder-stuff-core";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import Log, {Errors} from "@khan/logging";
import {
    PermissionsInfo,
    RequestInfo,
    type UserAttributes,
} from "@khan/shared-core";

import {canUserParticipateInABTesting} from "./can-user-participate-in-ab-testing.ts";

// How long we cache the feature-flag values for.
// sync-start:feature-flag-cache-interval 1407245634 https://github.com/Khan/webapp/blob/master/pkg/external/featureflags/api.go
const featureFlagCacheIntervalInMs = 5 * 60 * 1000;
// sync-end:feature-flag-cache-interval

// Returns a `GrowthBook` instance.  If `features` is not provided, it will
// default to `globalThis.__GROWTHBOOK_FEATURES__`.
export const getGrowthBook = (
    apiHost: string,
    userAttributes: UserAttributes | null,
    features: Record<string, FeatureDefinition<any>> = globalThis
        .__GROWTHBOOK_FEATURES__?.features,
): GrowthBook<Record<string, FeatureDefinition<any>>> => {
    // NOTE(kevinb): Even though we call growthbook.refreshFeatures() in GrowthbookProvider,
    // that doesn't refresh the cache when the GrowthBook instance is initially created. We
    // need to configure the cache to have a maxAge so that the cache is refreshed when the
    // GrowthBook instance is created.
    configureCache({
        maxAge: featureFlagCacheIntervalInMs,
    });

    // See https://docs.growthbook.io/lib/build-your-own#context for the complete
    // list of options.
    return new GrowthBook<Record<string, FeatureDefinition<any>>>({
        // NOTE(kevinb): Requests to https://www.khanacademy.org/api/features/sdk-u16yaYPyqdZ0OHuZ
        // are proxied to https://cdn.growthbook.io/api/features/sdk-u16yaYPyqdZ0OHuZ.  We want to
        // use this production URL even in dev.

        apiHost,
        clientKey: "sdk-u16yaYPyqdZ0OHuZ",
        // Disables all experiments (A/B tests).
        enabled: canUserParticipateInABTesting(userAttributes),
        // If this is defined, Growthbook won't query for the features automatically.  Since
        // __GROWTHBOOK_FEATURES__ is set via an edge-side include it also means that this
        // data will be available during SSR.
        features,
        // Dev mode allows the GrowthBook DevTools Chrome extension to connect to this instance
        // of GrowthBook.  This extension can be downloaded here:
        // https://chrome.google.com/webstore/detail/growthbook-devtools/opemhndcehfgipokneipaafbglcecjia
        enableDevMode:
            RequestInfo.IS_DEV_SERVER || PermissionsInfo.hasAnyPermissions,
        // We manually refresh features in growthbook-provider.tsx
        subscribeToChanges: false,
        // Disable streaming updates
        backgroundSync: false,
        trackingCallback: getTrackingCallback(features),
    });
};

// NOTE(kevinb): Exported for testing purposes only.
export const getTrackingCallback =
    (features: Record<string, FeatureDefinition<any>>) =>
    (experiment: Experiment<any>, result: Result<any>) => {
        const featureId = result.featureId;

        const ruleKeys = featureId ? getRuleKeys(features[featureId]) : [];
        if (!ruleKeys.includes(experiment.key)) {
            Log.error(
                "No featureId for the given experiment",
                Errors.Internal,
                {
                    sentryData: {
                        tags: {
                            growthbook: "true",
                            experimentKey: experiment.key,
                        },
                    },
                },
            );
        }

        // Depending on the hashAttribute, we want to publish different events.
        switch (result.hashAttribute) {
            case "threadId":
                publishAnalyticsEventForMinorConversion({
                    eventType: "ExperimentationPlatformThreadVariationAssigned",
                    eventSchemaVersion: 1,
                    experimentId: experiment.key,
                    experimentPhase: experiment.phase || "0",
                    variationId: result.key,
                    featureId: featureId ?? "",
                    featureHash: featureId
                        ? hashFeature(features[featureId])
                        : "",
                    threadId: result.hashValue,
                });
                break;
            default:
                publishAnalyticsEventForMinorConversion({
                    eventType: "ExperimentationPlatformVariationAssigned",
                    eventSchemaVersion: 3,
                    experimentId: experiment.key,
                    experimentPhase: experiment.phase || "0",
                    variationId: result.key,
                    featureId: featureId ?? "",
                    featureHash: featureId
                        ? hashFeature(features[featureId])
                        : "",
                });
        }
    };

const getRuleKeys = (feature: FeatureDefinition<any> | null): Array<string> => {
    if (!feature) {
        return [];
    }
    return (feature.rules || []).map((rule) => rule.key).filter(isNonNullable);
};

const hashFeature = (feature: FeatureDefinition<any>): string => {
    // sync-start:growthbook-feature-hash 522710567 ./dev/tools/get-growthbook-feature-hashes.ts
    const hash = stringHash(JSON.stringify(feature));
    // sync-end:growthbook-feature-hash
    return hash.toString();
};
